"use client";

/**
 * File is based on Builder.io's Image component, with modifications to fit our use case and tech stack.
 * https://github.com/BuilderIO/builder/blob/main/packages/sdks/src/blocks/image/image.lite.tsx
 */
import { Children, type CSSProperties, type ImgHTMLAttributes, type ReactNode } from "react";

import { isEditing } from "@builder.io/sdk-react";
import { getImageProps } from "next/image";

import { convertDimensionToInt } from "@/utils/convertDimensionToInt";

type BuilderImageProps = {
  altText?: string;
  aspectRatio?: number;
  backgroundPosition?:
    "center" | "top" | "left" | "right" | "bottom" | "top left" | "top right" | "bottom left" | "bottom right";
  backgroundSize?: "cover" | "contain";
  builderBlock?: BuilderBlock;
  children?: ReactNode;
  className?: string;
  fitContent?: boolean;
  height?: number | string;
  highPriority?: boolean;
  image?: string;
  lazy?: boolean;
  noWebp?: boolean;
  sizes?: string;
  src?: string;
  srcset?: string;
  title?: string;
  width?: number | string;
};

const DEFAULT_CLASS_NAME = "builder-image";
// Native <img srcset="... 100w, ..."> without an explicit sizes attribute
// is evaluated by the browser as if sizes were 100vw. We pass the same fallback
// to getImageProps so the optimized path keeps the original selection behavior.
const DEFAULT_BROWSER_SRCSET_SIZES = "100vw";

function updateQueryParam(uri = "", key: string, value: string | number | boolean): string {
  const expression = new RegExp(`([?&])${key}=.*?(&|$)`, "i");
  const separator = uri.includes("?") ? "&" : "?";

  if (uri.match(expression)) {
    return uri.replace(expression, `$1${key}=${encodeURIComponent(value)}$2`);
  }

  return `${uri}${separator}${key}=${encodeURIComponent(value)}`;
}

function getBuilderSrcSet(url: string): string {
  if (!url) {
    return url;
  }

  const sizes = [100, 200, 400, 800, 1200, 1600, 2000];

  if (url.match(/builder\.io/)) {
    let srcUrl = url;
    const widthInSrc = Number(url.split("?width=")[1]);

    if (!Number.isNaN(widthInSrc)) {
      srcUrl = `${srcUrl} ${widthInSrc}w`;
    }

    return sizes
      .filter((size) => size !== widthInSrc)
      .map((size) => `${updateQueryParam(url, "width", size)} ${size}w`)
      .concat([srcUrl])
      .join(", ");
  }

  return url;
}

function getSrcSetToUse({
  image,
  noWebp,
  src,
  srcset,
}: Pick<BuilderImageProps, "image" | "noWebp" | "src" | "srcset">) {
  const imageToUse = image || src;

  if (!imageToUse?.match(/builder\.io/)) {
    return srcset;
  }

  if (noWebp) {
    return undefined;
  }

  // Builder can provide a stale srcset that no longer matches the current asset URL.
  // When that happens, regenerate it from the image URL to preserve the original block behavior.
  if (srcset && image?.includes("builder.io/api/v1/image")) {
    if (!srcset.includes(image.split("?")[0]!)) {
      return getBuilderSrcSet(imageToUse);
    }

    return srcset;
  }

  if (image && !srcset) {
    return getBuilderSrcSet(imageToUse);
  }

  return getBuilderSrcSet(imageToUse);
}

function getWebpSrcSet(srcSetToUse?: string, noWebp?: boolean) {
  if (srcSetToUse?.match(/builder\.io/) && !noWebp) {
    return srcSetToUse.replace(/\?/g, "?format=webp&");
  }

  return undefined;
}

function isBuilderImage(url?: string) {
  return Boolean(url?.match(/builder\.io/));
}

function getOptimizedBuilderImageProps({
  altText,
  className,
  height,
  highPriority,
  imageSource,
  imageStyle,
  noWebp,
  sizes,
  title,
  width,
}: {
  altText?: string;
  className?: string;
  height?: number | string;
  highPriority?: boolean;
  imageSource?: string;
  imageStyle: CSSProperties;
  noWebp?: boolean;
  sizes?: string;
  title?: string;
  width?: number | string;
}): (Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & { key?: string; src: string }) | null {
  const resolvedWidth = convertDimensionToInt(width);
  const resolvedHeight = convertDimensionToInt(height);

  // Only switch to Next image optimization when we can reproduce the authored layout safely.
  // Missing dimensions or an explicit noWebp flag keep the original Builder-style rendering path.
  if (!imageSource || !isBuilderImage(imageSource) || noWebp || !resolvedWidth || !resolvedHeight) {
    return null;
  }

  try {
    const {
      props: { style: optimizedStyle, ...optimizedImageProps },
    } = getImageProps({
      alt: altText || "",
      height: resolvedHeight,
      priority: highPriority,
      sizes: sizes || DEFAULT_BROWSER_SRCSET_SIZES,
      src: imageSource,
      width: resolvedWidth,
    });

    return {
      ...optimizedImageProps,
      alt: altText || "",
      className: className ? `${DEFAULT_CLASS_NAME} ${className}` : DEFAULT_CLASS_NAME,
      key: isEditing() ? imageSource?.split("?")[0] : undefined,
      loading: highPriority ? "eager" : "lazy",
      role: altText ? undefined : "presentation",
      style: {
        ...optimizedStyle,
        ...imageStyle,
      },
      title,
    };
  } catch {
    // If Next rejects the image input for any reason, fall back to the native markup
    // instead of risking a broken image on already-authored Builder content.
    return null;
  }
}

export function BuilderImage({
  altText,
  aspectRatio,
  backgroundPosition,
  backgroundSize,
  builderBlock,
  children,
  className,
  fitContent = true,
  height,
  highPriority,
  image,
  noWebp,
  sizes,
  src,
  srcset,
  title,
  width,
}: BuilderImageProps) {
  const imageSource = image || src;
  const srcSetToUse = getSrcSetToUse({ image, noWebp, src, srcset });
  const webpSrcSet = getWebpSrcSet(srcSetToUse, noWebp);
  const hasAuthoredChildren = builderBlock ? Boolean(builderBlock.children?.length) : Children.count(children) > 0;

  const imageStyle: CSSProperties = {
    objectPosition: backgroundPosition || "center",
    objectFit: backgroundSize || "cover",
    // Builder uses absolute positioning when an aspect ratio box is present so the
    // image fills the reserved space while children can still be layered on top.
    ...(aspectRatio
      ? {
          position: "absolute",
          height: "100%",
          width: "100%",
          left: 0,
          top: 0,
        }
      : undefined),
  };
  const optimizedImageProps = getOptimizedBuilderImageProps({
    altText,
    className,
    height,
    highPriority,
    imageSource,
    imageStyle,
    noWebp,
    sizes,
    title,
    width,
  });
  const { key: optimizedImageKey, ...optimizedImagePropsWithoutKey } = optimizedImageProps || {};

  return (
    <>
      <picture data-testid="custom-image-component">
        {!optimizedImageProps && webpSrcSet ? <source srcSet={webpSrcSet} type="image/webp" sizes={sizes} /> : null}
        {optimizedImageProps ? (
          // Keep rendering a plain img element so the optimized path stays compatible
          // with the existing Builder markup structure and overlay behavior.
          // eslint-disable-next-line @next/next/no-img-element
          <img
            {...optimizedImagePropsWithoutKey}
            alt={optimizedImageProps.alt || ""}
            key={optimizedImageKey}
            {...{ fetchPriority: highPriority ? "high" : "auto" }}
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            loading={highPriority ? "eager" : "lazy"}
            {...{ fetchPriority: highPriority ? "high" : "auto" }}
            alt={altText || ""}
            title={title}
            key={isEditing() ? imageSource?.split("?")[0] : undefined}
            role={altText ? undefined : "presentation"}
            className={className ? `${DEFAULT_CLASS_NAME} ${className}` : DEFAULT_CLASS_NAME}
            src={imageSource}
            srcSet={srcSetToUse}
            sizes={sizes}
            style={imageStyle}
          />
        )}
      </picture>

      {aspectRatio && !(hasAuthoredChildren && fitContent) ? (
        <div
          className="builder-image-sizer"
          style={{
            width: "100%",
            paddingTop: `${aspectRatio * 100}%`,
            pointerEvents: "none",
            fontSize: 0,
          }}
        />
      ) : null}

      {hasAuthoredChildren && fitContent ? children : null}

      {hasAuthoredChildren && !fitContent ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        >
          {children}
        </div>
      ) : null}
    </>
  );
}
